import { Color } from '@ardoq/design-tokens';
import {
  Column as AGGridColumn,
  DefaultMenuItem,
  GetMainMenuItemsParams,
  IMenuActionParams,
  MenuItemDef,
  SizeColumnsToContentColumnLimits,
} from 'ag-grid-community';
import { CustomContextMenuItem, Row, SupportedDefaultMenuItem } from './types';
import { buildMenuItemIconSvg } from './theme';

export const isValidAutoSizeCap = (
  value: number | undefined
): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0;

const toColumnLimits = (
  capsByColId: ReadonlyMap<string, number>
): SizeColumnsToContentColumnLimits[] =>
  Array.from(capsByColId, ([colId, maxWidth]) => ({ colId, maxWidth }));

// AG Grid's localeText keys for the built-in autosize menu items. Reusing
// these keys means a consumer who already supplies translations via
// `localeText` automatically localises the capped variants too.
const AUTOSIZE_THIS_LOCALE_KEY = 'autosizeThisColumn';
const AUTOSIZE_ALL_LOCALE_KEY = 'autosizeAllColumns';
const AUTOSIZE_THIS_DEFAULT = 'Autosize This Column';
const AUTOSIZE_ALL_DEFAULT = 'Autosize All Columns';

const resolveLocaleText = (
  localeText: Record<string, string> | undefined,
  key: string,
  fallback: string
): string => localeText?.[key] ?? fallback;

const buildAutoSizeThisMenuItem = <RowData, RowId extends string = string>(
  capsByColId: ReadonlyMap<string, number>,
  localeText: Record<string, string> | undefined
): MenuItemDef<Row<RowData, RowId>> => ({
  name: resolveLocaleText(
    localeText,
    AUTOSIZE_THIS_LOCALE_KEY,
    AUTOSIZE_THIS_DEFAULT
  ),
  action: (params: IMenuActionParams<Row<RowData, RowId>>) => {
    const colId = params.column?.getColId();
    if (!colId) return;
    params.api.autoSizeColumns({
      colIds: [colId],
      columnLimits: toColumnLimits(capsByColId),
    });
  },
});

const buildAutoSizeAllMenuItem = <RowData, RowId extends string = string>(
  capsByColId: ReadonlyMap<string, number>,
  localeText: Record<string, string> | undefined
): MenuItemDef<Row<RowData, RowId>> => ({
  name: resolveLocaleText(
    localeText,
    AUTOSIZE_ALL_LOCALE_KEY,
    AUTOSIZE_ALL_DEFAULT
  ),
  action: (params: IMenuActionParams<Row<RowData, RowId>>) => {
    params.api.autoSizeAllColumns({
      columnLimits: toColumnLimits(capsByColId),
    });
  },
});

const getMainMenuSortItems = (
  column: AGGridColumn | null
): DefaultMenuItem[] => {
  if (!column || !column.isSortable()) {
    return [];
  }
  if (column.getSort() === 'asc') {
    return ['sortDescending', 'sortUnSort'];
  }
  if (column.getSort() === 'desc') {
    return ['sortAscending', 'sortUnSort'];
  }
  return ['sortAscending', 'sortDescending'];
};

const getMainMenuGroupingColumnItems = (
  column: AGGridColumn | null
): DefaultMenuItem[] => {
  if (!column) {
    return [];
  }

  if (column.getId() !== 'ag-Grid-AutoColumn') {
    return [];
  }

  return ['expandAll', 'contractAll'];
};

export const getMenuItems = (
  params: GetMainMenuItemsParams,
  pinnedColumns: boolean,
  enableColumnChooser: boolean,
  groupingEnabled: boolean
): DefaultMenuItem[] => {
  const sortItems = getMainMenuSortItems(params.column);
  const groupingColumnItems = getMainMenuGroupingColumnItems(params.column);
  const pinItems: DefaultMenuItem[] = pinnedColumns
    ? (['pinSubMenu'] as const)
    : [];
  const chooserItems: DefaultMenuItem[] = enableColumnChooser
    ? (['columnChooser', 'resetColumns'] as const)
    : [];

  const groupedColumnItems: DefaultMenuItem[] = groupingEnabled
    ? ['rowUnGroup']
    : [];

  const menuItemGroups: DefaultMenuItem[][] = [
    sortItems,
    pinItems,
    chooserItems,
    groupingColumnItems,
    groupedColumnItems,
  ].filter(items => items.length > 0);

  const mainMenuItems: DefaultMenuItem[] = menuItemGroups.reduce<
    DefaultMenuItem[]
  >((acc, items) => {
    if (acc.length > 0) {
      acc.push('separator');
    }
    acc.push(...items);
    return acc;
  }, []);

  return mainMenuItems;
};

const toColorClassName = (colorToken: Color): string =>
  `ag-menu-item-color-${colorToken}`;

const toMenuItemColorCssClass = <RowData, RowId extends string = string>(
  item: CustomContextMenuItem<RowData, RowId>
) => {
  const existingClasses = item.cssClasses ?? [];

  if (!item.color) {
    return existingClasses;
  }

  const className = toColorClassName(item.color);
  return [className, ...existingClasses];
};

export const transformContextMenuItems = <
  RowData,
  RowId extends string = string,
>(
  items: (SupportedDefaultMenuItem | CustomContextMenuItem<RowData, RowId>)[],
  rowData: RowData,
  autoSizeCaps?: ReadonlyMap<string, number>,
  localeText?: Record<string, string>
): (DefaultMenuItem | MenuItemDef<Row<RowData, RowId>>)[] => {
  const hasCaps = autoSizeCaps !== undefined && autoSizeCaps.size > 0;
  return items.map(item => {
    if (typeof item === 'string') {
      if (hasCaps && item === 'autoSizeThis') {
        return buildAutoSizeThisMenuItem<RowData, RowId>(
          autoSizeCaps,
          localeText
        );
      }
      if (hasCaps && item === 'autoSizeAll') {
        return buildAutoSizeAllMenuItem<RowData, RowId>(
          autoSizeCaps,
          localeText
        );
      }
      return item;
    }

    return {
      ...item,
      action: () => {
        item.onClick(rowData);
      },
      cssClasses: toMenuItemColorCssClass(item),
      icon: item.icon ? buildMenuItemIconSvg(item.icon.name) : undefined,
    };
  });
};
